<template>
    <InputNullableDateTime
        :model-value="modelValue"
        :format="format"
        :validator="validator"
        :hide-selector="hideSelector"
        :show-null-setter="showNullSetter"
        :auto-fill-on-blur="autoFillOnBlur"
        :prefill-date="prefillDate"
        :default-date-for-time-only-input="defaultDateForTimeOnlyInput"
        :disabled="disabled"
        @change="change"
        @update:model-value="updateModelValue"
    />
</template>
<script lang="ts" setup>
import type { ValidatorDef } from '@/objects/gui';
import type { DateType } from '@/utilities/dateutils';
import InputNullableDateTime from './InputNullableDateTime.vue';

const props = withDefaults(
    defineProps<{
        modelValue: Date;

        /**
         * The format to use.
         */
        format: DateType;

        /**
         * The validator for the input
         * @default null
         */
        validator?: ValidatorDef | null;

        /**
         * If the date/time selector should be hidden
         * @default false
         */
        hideSelector?: boolean;

        /**
         * If the nullsetter should be shown
         * @default false
         * */
        showNullSetter?: boolean;

        /**
         * Autofills missing values in the timepart when the user blurs the input
         *
         * This allows for fast partial input of the time
         *
         * This only fills the timepart, not the datepart since the datepart doesnt have a natural way to autofill
         *
         * Atleast the hour part of the time has to be filled, anything else will be set to 0
         *
         * Practically this means that you can put 12:3 and it will be autofilled to 12:30:00.000
         */
        autoFillOnBlur?: boolean;

        /**
         * Date that will prefill the datePart of the box
         */
        prefillDate?: Date;

        /**
         * Default date to use when only time is shown, when not filled we use defaultDateTimeForIncompleteFormats from dateutils
         */
        defaultDateForTimeOnlyInput?: Date;

        disabled?: boolean;
    }>(),
    {
        validator: null,
        hideSelector: false,
        showNullSetter: false,
        prefillMs: false,
        autoFillOnBlur: false,
    },
);

const emits = defineEmits<{
    (e: 'update:modelValue', val: Date | null): void;
    (e: 'change', newval: Date, oldval: Date | null): void;
}>();

const change = (newval: Date | null, oldval: Date | null) => {
    if (newval === null || oldval === null) {
        return;
    }
    emits('change', newval, oldval);
};

const updateModelValue = (val: Date | null) => {
    if (val === null) {
        return;
    }

    emits('update:modelValue', val);
};
</script>
